import { plateFont, numFont } from '../../utils/fonts';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventIcon from '@mui/icons-material/Event';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import { formatDate } from '../../utils';
import CustomerDialog from './CustomerDialog';
import PurchaseHistoryList from './PurchaseHistoryList';
import VehicleDetailsDialog from '../vehicles/VehicleDetailsDialog';
import NeedsReplacementChip from '../tire_hotel/NeedsReplacementChip';
import { InternalNoteEditor } from '../shared/InternalNote';
import usePermissions from '../../hooks/usePermissions';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Info Item component
function InfoItem({ label, value, icon, xs = 12, sm = 6 }) {
  return (
    <Grid item xs={xs} sm={sm}>
      <Paper variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {icon && <Box sx={{ color: 'text.disabled', flexShrink: 0 }}>{icon}</Box>}
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {value || '-'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}

// Basic Info Tab Component
function BasicInfoTab({ customer, t, onEdit }) {
  if (!customer) return null;

  const isCompany = customer.type === 'company';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>{t('customer.basicInfo')}</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={onEdit}
        >
          {t('Edit')}
        </Button>
      </Box>

      <Grid container spacing={2}>
        <InfoItem
          label={t('Customer Type')}
          value={isCompany ? t('Company') : t('Individual')}
          icon={isCompany ? <BusinessIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
        />
        <InfoItem label={t('Name')} value={customer.name} />

        {isCompany && (
          <>
            <InfoItem label={t('Contact Person')} value={customer.contact_person} />
            <InfoItem label={t('Business ID')} value={customer.business_id} />
          </>
        )}

        <InfoItem label={t('Email')} value={customer.email} icon={<EmailIcon fontSize="small" />} />
        <InfoItem label={t('Phone')} value={customer.phone} icon={<PhoneIcon fontSize="small" />} />
        <InfoItem label={t('Address')} value={customer.address_formatted || customer.address} icon={<HomeIcon fontSize="small" />} xs={12} sm={12} />

        {isCompany && customer.default_buyers_reference && (
          <InfoItem
            label={t('Default buyers reference')}
            value={customer.default_buyers_reference}
            icon={<ReceiptIcon fontSize="small" />}
          />
        )}

        {customer.payment_terms_days != null && (
          <InfoItem
            label={t('customer.paymentTerms')}
            value={`${customer.payment_terms_days} ${t('days')}`}
            icon={<ReceiptIcon fontSize="small" />}
          />
        )}

        {isCompany && customer.verkkolasku_address && (
          <InfoItem
            label={t('E-Invoice Address')}
            value={customer.verkkolasku_address}
            icon={<ReceiptIcon fontSize="small" />}
            xs={12}
            sm={12}
          />
        )}
      </Grid>
    </Box>
  );
}

// Maintenance History Tab Component
function MaintenanceHistoryTab({ customerId, t, onMaintenanceClick, onVehicleClick }) {
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMaintenances = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/maintenances/?customer_id=${customerId}&ordering=-id&state=completed,picked_up`);
        const data = response.data;
        setMaintenances(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchMaintenances();
    }
  }, [customerId]);

  const getStateColor = (state) => {
    switch (state) {
      case 'planned': return 'default';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'picked_up': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (maintenances.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('customer.noMaintenances')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {maintenances.map((maintenance) => (
        <Paper
          key={maintenance.id}
          variant="outlined"
          sx={{
            p: 2,
            mb: 1.5,
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box
              onClick={() => onMaintenanceClick(maintenance.id)}
              sx={{ cursor: 'pointer', flex: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    ...numFont,
                    fontSize: '14px',
                  }}
                >
                  #{maintenance.work_number}
                </Typography>
                <Chip
                  label={t(maintenance.state_display || maintenance.state)}
                  size="small"
                  color={getStateColor(maintenance.state)}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <EventIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {formatDate(maintenance.created_at)?.split(' ')[0]}
              </Typography>
            </Box>
          </Box>

          {/* Vehicle info - clickable */}
          {maintenance.vehicle && (
            <Box
              onClick={() => onVehicleClick(maintenance.vehicle.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <DirectionsCarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography
                variant="body2"
                sx={{
                  ...plateFont,
                  fontWeight: 500,
                }}
              >
                {maintenance.vehicle.registration_number}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {maintenance.vehicle.manufacturer} {maintenance.vehicle.model}
              </Typography>
            </Box>
          )}

          {maintenance.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {maintenance.description}
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
}

// Billing History Tab Component
function BillingHistoryTab({ customerId, t, onInvoiceClick }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/invoices/?customer_id=${customerId}&ordering=-id&status=sent,paid`);
        const data = response.data;
        setInvoices(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchInvoices();
    }
  }, [customerId]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'sent': return 'primary';
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (invoices.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('customer.noInvoices')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {invoices.map((invoice) => (
        <Paper
          key={invoice.id}
          variant="outlined"
          onClick={() => onInvoiceClick(invoice.id)}
          sx={{
            p: 2,
            mb: 1.5,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ReceiptIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                <Typography
                  sx={{
                    fontWeight: 600,
                    ...numFont,
                    fontSize: '14px',
                  }}
                >
                  {invoice.invoice_number}
                </Typography>
                <Chip
                  label={t(invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1))}
                  size="small"
                  color={getStatusColor(invoice.status)}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <EventIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {formatDate(invoice.invoice_date)?.split(' ')[0]}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '15px',
              }}
            >
              {formatCurrency(invoice.total_incl_vat)}
            </Typography>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

// Vehicles Tab Component
function CustomerVehiclesTab({ customerId, t, onVehicleClick }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`/api/vehicles/by-customer/?customer_id=${encodeURIComponent(customerId)}`)
      .then((response) => {
        if (!cancelled) setVehicles(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setVehicles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (vehicles.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('customer.noVehicles')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('customer.vehiclesHelp')}
      </Typography>
      {vehicles.map((vehicle) => (
        <Paper
          key={vehicle.id}
          variant="outlined"
          onClick={() => onVehicleClick(vehicle.id)}
          data-testid="customer-vehicle-row"
          sx={{
            p: 2,
            mb: 1.5,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            transition: 'all 0.15s ease',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
          }}
        >
          <DirectionsCarIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          <Box>
            <Typography
              variant="body2"
              sx={{ ...plateFont, fontWeight: 600 }}
            >
              {vehicle.registration_number}
            </Typography>
            {(vehicle.manufacturer || vehicle.model) && (
              <Typography variant="caption" color="text.secondary">
                {[vehicle.manufacturer, vehicle.model].filter(Boolean).join(' ')}
                {vehicle.year_model ? ` (${vehicle.year_model})` : ''}
              </Typography>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

// Tire Hotel Cards Tab Component
function TireHotelCardsTab({ customerId, t, onCardClick, onVehicleClick }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'stored': return 'success';
      case 'on_vehicle': return 'info';
      case 'disposed': return 'default';
      default: return 'default';
    }
  };

  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/tire-hotel-cards/?customer_id=${customerId}`);
        const data = response.data;
        setCards(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCards();
    }
  }, [customerId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (cards.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('tire_hotel.no_customer_cards')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {cards.map((card) => (
        <Paper
          key={card.id}
          variant="outlined"
          onClick={() => onCardClick(card.id)}
          sx={{
            p: 2,
            mb: 1.5,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TireRepairIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                <Typography variant="body2" fontWeight={600}>
                  {card.tire_size_display || '-'} {card.tire_brand || ''}
                </Typography>
              </Box>
              {card.vehicle_registration_number && (
                <Box
                  onClick={(event) => {
                    // The Paper opens the card; the plate opens the vehicle behind it
                    event.stopPropagation();
                    if (card.vehicle) onVehicleClick(card.vehicle);
                  }}
                  data-testid="customer-hotel-card-vehicle-link"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 0.5,
                    cursor: card.vehicle ? 'pointer' : 'default',
                    '&:hover': { color: card.vehicle ? 'primary.main' : 'inherit' },
                  }}
                >
                  <DirectionsCarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      ...plateFont,
                      fontWeight: 500,
                    }}
                  >
                    {card.vehicle_registration_number}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={t(`tire_hotel.${card.status}`)}
                  color={getStatusColor(card.status)}
                />
                <NeedsReplacementChip card={card} t={t} />
                <Chip
                  size="small"
                  label={t(`tire_hotel.${card.tire_set_type}`)}
                  variant="outlined"
                />
              </Box>
            </Box>
            {card.location_label && (
              <Typography variant="body2" color="text.secondary">
                {card.location_label}
              </Typography>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

function CustomerDetailsDialog({ open, onClose, customerId, onCustomerUpdated }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const tireHotelEnabled = currentOrganization?.tire_hotel_enabled ?? false;
  const [tab, setTab] = useState(0);
  const { can } = usePermissions();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [vehicleDetailsOpen, setVehicleDetailsOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/customers/${customerId}/`);
      setCustomer(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) {
      fetchCustomer();
      setTab(0);
    }
  }, [open, customerId, fetchCustomer]);

  const handleMaintenanceClick = (maintenanceId) => {
    onClose();
    navigate(`/maintenances/${maintenanceId}`);
  };

  const handleInvoiceClick = (invoiceId) => {
    onClose();
    navigate(`/invoices/${invoiceId}`);
  };

  const handleVehicleClick = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    setVehicleDetailsOpen(true);
  };

  const handleHotelCardClick = (cardId) => {
    onClose();
    navigate(`/tire-hotel/${cardId}`);
  };

  const handleSaveInternalNote = async (text) => {
    const response = await axios.patch(`/api/customers/${customer.id}/`, { internal_note: text });
    setCustomer(response.data);
    if (onCustomerUpdated) {
      onCustomerUpdated(response.data);
    }
  };

  const handleEditSave = (updatedCustomer) => {
    setCustomer(updatedCustomer);
    setEditDialogOpen(false);
    if (onCustomerUpdated) {
      onCustomerUpdated(updatedCustomer);
    }
  };

  if (!customerId) return null;

  const isCompany = customer?.type === 'company';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '50vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: isCompany ? 'rgba(49, 130, 206, 0.1)' : 'rgba(72, 187, 120, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isCompany ? 'secondary.main' : 'success.main',
              }}
            >
              {isCompany ? <BusinessIcon /> : <PersonIcon />}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {customer?.name || '...'}
              </Typography>
              {customer?.email && (
                <Typography variant="body2" color="text.secondary">
                  {customer.email}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab label={t('customer.basicInfo')} icon={<PersonIcon />} iconPosition="start" />
            <Tab label={t('customer.maintenanceHistory')} icon={<BuildIcon />} iconPosition="start" />
            <Tab label={t('customer.billingHistory')} icon={<ReceiptIcon />} iconPosition="start" />
            <Tab label={t('customer.purchaseHistory')} icon={<Inventory2OutlinedIcon />} iconPosition="start" data-testid="customer-purchase-history-tab" />
            <Tab label={t('customer.vehicles')} icon={<DirectionsCarIcon />} iconPosition="start" data-testid="customer-vehicles-tab" />
            {tireHotelEnabled && (
              <Tab label={t('tire_hotel.hotel_cards')} icon={<TireRepairIcon />} iconPosition="start" />
            )}
          </Tabs>
        </Box>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <TabPanel value={tab} index={0}>
                {customer && (
                  <InternalNoteEditor
                    kind="customer"
                    note={customer.internal_note}
                    canEdit={can('customers.manage')}
                    onSave={handleSaveInternalNote}
                    sx={{ mb: 2 }}
                  />
                )}
                <BasicInfoTab
                  customer={customer}
                  t={t}
                  onEdit={() => setEditDialogOpen(true)}
                />
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <MaintenanceHistoryTab
                  customerId={customerId}
                  t={t}
                  onMaintenanceClick={handleMaintenanceClick}
                  onVehicleClick={handleVehicleClick}
                />
              </TabPanel>

              <TabPanel value={tab} index={2}>
                <BillingHistoryTab
                  customerId={customerId}
                  t={t}
                  onInvoiceClick={handleInvoiceClick}
                />
              </TabPanel>

              <TabPanel value={tab} index={3}>
                <PurchaseHistoryList customerId={customerId} onInvoiceClick={handleInvoiceClick} />
              </TabPanel>

              <TabPanel value={tab} index={4}>
                <CustomerVehiclesTab
                  customerId={customerId}
                  t={t}
                  onVehicleClick={handleVehicleClick}
                />
              </TabPanel>

              {tireHotelEnabled && (
                <TabPanel value={tab} index={5}>
                  <TireHotelCardsTab
                    customerId={customerId}
                    t={t}
                    onCardClick={handleHotelCardClick}
                    onVehicleClick={handleVehicleClick}
                  />
                </TabPanel>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <CustomerDialog
        open={editDialogOpen}
        handleClose={() => setEditDialogOpen(false)}
        customer={customer}
        updateCustomer={handleEditSave}
        addCreatedCustomer={() => {}}
        onDelete={async (customerId) => {
          await axios.delete(`/api/customers/${customerId}/`);
          setEditDialogOpen(false);
          onClose();
          if (onCustomerUpdated) onCustomerUpdated(null);
        }}
      />

      {/* Vehicle Details Dialog */}
      <VehicleDetailsDialog
        open={vehicleDetailsOpen}
        onClose={() => setVehicleDetailsOpen(false)}
        vehicleId={selectedVehicleId}
      />
    </>
  );
}

export default CustomerDetailsDialog;
